import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, LogOut, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PendingApprovalPage({ user }) {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7]">
      {/* Header */}
      <nav className="bg-white border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-2.5">
              <Shield className="w-7 h-7 text-brand" strokeWidth={2.2} />
              <h1 className="text-lg font-bold text-[#222]">
                Sergeant TAP
              </h1>
            </div>
            <Button
              data-testid="pending-logout-btn"
              onClick={handleLogout}
              disabled={loggingOut}
              variant="ghost"
              className="text-[#999] hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="p-8 text-center border border-slate-200" data-testid="pending-approval-card">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Account Pending Approval
          </h1>
          
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Thank you for registering as an employer on Sergeant TAP. Your account is currently 
            under review by a regional administrator.
          </p>

          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-slate-900 mb-2">What happens next?</h3>
            <ul className="text-sm text-slate-600 text-left space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-brand mt-1">1.</span>
                <span>A regional administrator will review your company information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand mt-1">2.</span>
                <span>You&apos;ll receive an email notification once approved</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand mt-1">3.</span>
                <span>After approval, you can post jobs and search candidates</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              className="border-slate-300"
              onClick={() => window.location.href = 'mailto:support@sergeanttap.com'}
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
            <Button
              onClick={handleLogout}
              disabled={loggingOut}
              variant="ghost"
              className="text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>

          {user?.email && (
            <p className="text-xs text-slate-400 mt-6">
              Logged in as {user.email}
            </p>
          )}
        </Card>
      </main>
    </div>
  );
}

export default PendingApprovalPage;
